// Gear rail copy tables and pure formatters — labels.ts owns exactly what the
// UI brief's tables say and nothing else (docs/plans/2026-09-14-av-gear-ui-
// brief.md "Labels", "Price" and "Spec rows by specs kind"). No DOM, no
// catalogue loading: every export here is a pure function of a `Product`.

import type { CategoryKey, Product, ProductType } from '@roomtreatment/av-catalog';
import type { HeadroomBand } from '@roomtreatment/av-compat';
import { fmtPanelDims, type Units } from '@roomtreatment/core';

/**
 * The type-label table from the UI brief, verbatim. MUST stay byte-identical
 * to `packages/av-catalog/src/search.ts`'s private `TYPE_LABEL` (search needs
 * its own copy — av-catalog is a dependency-free leaf package and can't
 * import from `web`).
 */
export const TYPE_LABELS: Record<ProductType, string> = {
  speaker_bookshelf: 'Bookshelf',
  speaker_tower: 'Tower',
  speaker_center: 'Center',
  speaker_surround: 'Surround',
  speaker_height: 'Height',
  speaker_in_ceiling: 'In-ceiling',
  subwoofer: 'Subwoofer',
  av_receiver: 'Receiver',
  av_processor: 'Processor',
  // §3 "Role explanations": the per-product type label stays short — "Power
  // amp", singular — distinct from the rail SECTION heading "Power amps"
  // (plural, browse.ts's BrowseCategory label for the same product type).
  power_amp: 'Power amp',
  tv: 'TV',
  projector: 'Projector',
  projector_screen: 'Screen',
};

/**
 * §1 "Concepts we must make obvious" — exact one-line copy, keyed by the type
 * it explains. Only the three electronics types get one: the brief's table
 * also covers passive/active speakers, but those appear as prose in the
 * checklist's needs block (RoomNeedsBlock.tsx), not attached to a product
 * type label. Shown under the type label in the product view
 * (GearProductView.tsx, the parts picker's detail panel).
 */
export const ROLE_EXPLANATION: Partial<Record<ProductType, string>> = {
  av_receiver: 'Decodes surround sound, switches inputs and powers your speakers.',
  av_processor: "Does a receiver's job without the amplifiers. Pair it with a power amp.",
  power_amp: 'Only powers speakers. It needs a receiver or processor in front of it.',
};

/**
 * One line under a category's heading on the public gear pages (/gear,
 * /gear/c/…). "Receivers and processors" holds both types, and the difference
 * between them is exactly what owners don't know, so its line names both.
 * "Power amps" has one type.
 */
export const CATEGORY_EXPLANATION: Partial<Record<CategoryKey, string>> = {
  receivers: 'A receiver powers your speakers. A processor does the same job without the amplifiers, so it needs a power amp.',
  amplifiers: ROLE_EXPLANATION.power_amp,
};

/** The affiliate disclosure above every block of buy links, verbatim from
 *  docs/plans/2026-09-14-av-gear-spec.md §8.6. /disclosure quotes it. */
export const OFFER_DISCLOSURE =
  'We earn a commission when you buy through these links. It never affects which products we show or recommend.';

/** §3 "Headroom band" copy column, verbatim. */
export const HEADROOM_BAND_LABEL: Record<HeadroomBand, string> = {
  'too-weak': 'Too weak',
  'just-enough': 'Just enough',
  comfortable: 'Comfortable',
  'more-than-needed': 'More than you need',
};

const USD0 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** `$700 a pair` or `$1,799` — MSRP rounded to whole dollars. */
export function formatPrice(product: Pick<Product, 'msrpUsd' | 'sellUnit'>): string {
  const amount = USD0.format(Math.round(product.msrpUsd.v));
  return product.sellUnit === 'pair' ? `${amount} a pair` : amount;
}

/** `$1,799` on its own — the shopping-card layout (brief docs/plans/2026-
 *  09-15-gear-v2-brief.md §3) renders the amount large and the "a pair" unit
 *  small beside it, so the two need separate strings rather than
 *  `formatPrice`'s single combined one. */
export function priceAmount(product: Pick<Product, 'msrpUsd'>): string {
  return USD0.format(Math.round(product.msrpUsd.v));
}

/** `a pair`, or null for a product sold `each` (nothing to render). */
export function priceUnitLabel(product: Pick<Product, 'sellUnit'>): string | null {
  return product.sellUnit === 'pair' ? 'a pair' : null;
}

export interface SpecRow {
  label: string;
  value: string;
}

function formatDrivers(drivers: readonly { nominalMm: number; count: number }[]): string {
  return drivers.map((d) => `${d.count} × ${Math.round(d.nominalMm)} mm`).join(', ');
}

function sizeRow(product: Pick<Product, 'body'>, units: Units): string {
  return fmtPanelDims(units, product.body.w / 1000, product.body.h / 1000, product.body.d / 1000);
}

function subwooferInputsLabel(inputs: { lineLevel: boolean; highLevel: boolean }): string | null {
  if (inputs.lineLevel && inputs.highLevel) return 'Line, speaker-level';
  if (inputs.lineLevel) return 'Line';
  if (inputs.highLevel) return 'Speaker-level';
  return null;
}

function ampInputsLabel(inputs: { rca: boolean; xlr: boolean }): string | null {
  if (inputs.rca && inputs.xlr) return 'RCA, XLR';
  if (inputs.rca) return 'RCA';
  if (inputs.xlr) return 'XLR';
  return null;
}

/** `1.5` (fixed lens) or `1.2 – 1.5` (zoom). */
function throwRatioLabel(r: { min: number; max: number }): string {
  return r.min === r.max ? `${r.min}` : `${r.min} – ${r.max}`;
}

const SCREEN_FORMAT_LABEL: Record<'fixed' | 'pull-down' | 'motorized', string> = {
  fixed: 'Fixed frame',
  'pull-down': 'Pull-down',
  motorized: 'Motorized',
};

function screenFormatLabel(format: 'fixed' | 'pull-down' | 'motorized'): string {
  return SCREEN_FORMAT_LABEL[format];
}

/** `Dolby Atmos, DTS:X` / `Dolby Atmos` / `None` — the brief's three cases,
 *  generalized: a DTS:X-only unit (not in the brief's examples but not
 *  excluded either) reads `DTS:X` by the same construction. */
function heightFormatsLabel(heightDecoding: { atmos: boolean; dtsx: boolean }): string {
  const formats = [heightDecoding.atmos && 'Dolby Atmos', heightDecoding.dtsx && 'DTS:X'].filter(
    (v): v is string => !!v,
  );
  return formats.length > 0 ? formats.join(', ') : 'None';
}

/**
 * Spec rows for the product view, in the brief's fixed order per kind. A row
 * whose value is absent (an optional spec the product doesn't publish) is
 * omitted — never rendered with a placeholder.
 */
export function specRowsFor(product: Product, units: Units): SpecRow[] {
  const { specs } = product;
  const rows: SpecRow[] = [];

  if (specs.kind === 'speaker') {
    if (specs.active.v) {
      rows.push({ label: 'Powered', value: 'Yes' });
    } else if (specs.impedanceOhm) {
      rows.push({ label: 'Impedance', value: `${specs.impedanceOhm.v} Ω` });
    }
    if (specs.sensitivityDb) rows.push({ label: 'Sensitivity', value: `${specs.sensitivityDb.v} dB` });
    if (specs.lowHz) rows.push({ label: 'Low end', value: `${Math.round(specs.lowHz.v)} Hz` });
    rows.push({ label: 'Drivers', value: formatDrivers(specs.drivers.v) });
    rows.push({ label: 'Size', value: sizeRow(product, units) });
    return rows;
  }

  if (specs.kind === 'subwoofer') {
    rows.push({ label: 'Amplifier', value: `${Math.round(specs.ampW.v)} W` });
    rows.push({ label: 'Drivers', value: formatDrivers(specs.drivers.v) });
    rows.push({ label: 'Low end', value: `${Math.round(specs.lowHz.v)} Hz` });
    const inputs = subwooferInputsLabel(specs.inputs.v);
    if (inputs) rows.push({ label: 'Inputs', value: inputs });
    rows.push({ label: 'Size', value: sizeRow(product, units) });
    return rows;
  }

  if (specs.kind === 'av') {
    rows.push({ label: 'Amplifier channels', value: `${specs.ampChannels.v}` });
    rows.push({ label: 'Processing channels', value: `${specs.processingChannels.v}` });
    rows.push({ label: 'Height formats', value: heightFormatsLabel(specs.heightDecoding.v) });
    if (specs.power) rows.push({ label: 'Power', value: `${Math.round(specs.power.w8)} W at 8 Ω` });
    if (specs.roomCorrection) rows.push({ label: 'Room correction', value: specs.roomCorrection.v });
    rows.push({ label: 'Size', value: sizeRow(product, units) });
    return rows;
  }

  if (specs.kind === 'power_amp') {
    rows.push({ label: 'Channels', value: `${specs.channels.v}` });
    rows.push({ label: 'Power', value: `${Math.round(specs.power.w8)} W at 8 Ω` });
    rows.push({ label: 'Lowest impedance', value: `${specs.minImpedanceOhm.v} Ω` });
    const inputs = ampInputsLabel(specs.inputs.v);
    if (inputs) rows.push({ label: 'Inputs', value: inputs });
    rows.push({ label: 'Size', value: sizeRow(product, units) });
    return rows;
  }

  if (specs.kind === 'tv') {
    rows.push({ label: 'Screen size', value: `${Math.round(specs.screenIn.v)}"` });
    rows.push({ label: 'Resolution', value: specs.resolution.v });
    rows.push({ label: 'Refresh rate', value: `${Math.round(specs.refreshHz.v)} Hz` });
    rows.push({ label: 'HDMI inputs', value: `${specs.hdmiInputs.v}` });
    rows.push({ label: 'eARC', value: specs.earc.v ? 'Yes' : 'No' });
    rows.push({ label: 'Size', value: sizeRow(product, units) });
    return rows;
  }

  if (specs.kind === 'projector') {
    rows.push({ label: 'Brightness', value: `${Math.round(specs.lumensAnsi.v)} ANSI lumens` });
    rows.push({ label: 'Resolution', value: specs.resolution.v });
    rows.push({ label: 'Throw ratio', value: throwRatioLabel(specs.throwRatio.v) });
    rows.push({ label: 'HDMI inputs', value: `${specs.hdmiInputs.v}` });
    rows.push({ label: 'Size', value: sizeRow(product, units) });
    return rows;
  }

  // projector_screen
  rows.push({ label: 'Screen size', value: `${Math.round(specs.diagonalIn.v)}"` });
  rows.push({ label: 'Aspect', value: specs.aspect.v });
  rows.push({ label: 'Gain', value: `${specs.gain.v}` });
  rows.push({ label: 'Format', value: screenFormatLabel(specs.format.v) });
  rows.push({ label: 'Acoustically transparent', value: specs.acousticallyTransparent.v ? 'Yes' : 'No' });
  rows.push({ label: 'Size', value: sizeRow(product, units) });
  return rows;
}
